import type {
  BenchSortType,
  ConversionCreditCampaign,
  SortingOption,
} from '__generated__/globalTypes';
import { session as baseSessionStorage } from 'lib/storage';

export enum SESSION_STORAGE {
  forcedEnv = 'forcedEnv',
  forcedFlags = 'forcedFlags',
  forcedFlag = 'forcedFlag',
  sport = 'sport',
  signupPromo = 'signup-promo',
  mobileApp = 'mobileApp',
  deeplink = 'deeplink',
  appBuildVersionNumber = 'appBuildVersionNumber',
  googlePlayStoreCountryCode = 'googlePlayStoreCountryCode',
  sawPaidCardExplanatoryDialog = 'sawPaidCardExplanatoryDialog',
  debugI18n = 'debugI18n',
  dismissedLineupPredictionTimerGWs = 'dismissedLineupPredictionTimerGWs',
  composeTeamSelection = 'composeTeamSelection',
  expandedGroupSlugPerFixture = 'expandedGroupSlugPerFixture',
  composeFilters = 'composeFilters',
  storefrontCountryCode = 'storefrontCountryCode',
  bestPackCardPictureUrl = 'bestPackCardPictureUrl',
}
export enum IOsMobileAppVersion {
  IOS_1 = 'ios-1',
  IOS_GOLD = 'ios-gold',
}

const isIOsMobileAppVersion = (value: string | null) =>
  !!value && (Object.values(IOsMobileAppVersion) as string[]).includes(value);

export type MobileAppVersion = 1 | 2 | 3 | 4 | IOsMobileAppVersion;
export type MobileAppBuildVersion = number;

export type SessionStorageEntries = {
  [SESSION_STORAGE.signupPromo]: Pick<
    ConversionCreditCampaign,
    'campaignCode'
  > & {
    endDate: string;
  };
  [SESSION_STORAGE.deeplink]: string;
  [SESSION_STORAGE.mobileApp]: MobileAppVersion;
  [SESSION_STORAGE.appBuildVersionNumber]: MobileAppBuildVersion;
  [SESSION_STORAGE.googlePlayStoreCountryCode]: string;
  [SESSION_STORAGE.sawPaidCardExplanatoryDialog]: string;
  [SESSION_STORAGE.dismissedLineupPredictionTimerGWs]: string[];
  [SESSION_STORAGE.composeTeamSelection]: {
    so5LeaderboardSlug: string;
    composeSessionId: string;
    benchObjectIds: Nullable<string>[];
  };
  [SESSION_STORAGE.expandedGroupSlugPerFixture]: Record<string, string[]>;
  [SESSION_STORAGE.composeFilters]: {
    includeUsed?: boolean;
    includeNoGame?: boolean;
    startersOnly?: boolean;
    confirmedStartersOnly?: boolean;
    sortType?: { type: BenchSortType; direction: SortingOption };
  };
  [SESSION_STORAGE.storefrontCountryCode]: string;
  [SESSION_STORAGE.bestPackCardPictureUrl]: string;
};

const getValue = <K extends keyof SessionStorageEntries>(key: K) => {
  const stringValue = baseSessionStorage.getItem(key);
  if (isIOsMobileAppVersion(stringValue)) {
    return stringValue as SessionStorageEntries[K];
  }
  if (stringValue) {
    try {
      return JSON.parse(stringValue) as SessionStorageEntries[K];
    } catch {
      return null;
    }
  }

  return null;
};

const setValue = <K extends keyof SessionStorageEntries>(
  key: K,
  v: SessionStorageEntries[K] | null
) => {
  if (v !== null) {
    baseSessionStorage.setItem(key, JSON.stringify(v));
  } else {
    baseSessionStorage.removeItem(key);
  }
  window.dispatchEvent(
    new StorageEvent('storage', {
      key,
      newValue: v !== null ? JSON.stringify(v) : null,
    })
  );
};

export const sessionStorage = {
  getValue,
  setValue,
};
